import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  Button,
  Dialog,
  SegmentedControl,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import {
  DEFAULT_UNPAID_INVOICE_FILTERS,
  UnpaidInvoiceFilters,
  UnpaidInvoiceRecordTypeFilter,
} from '@finance-hub/domain/invoicing/blocs/types/unpaid-invoice-list';

const DIALOG_WIDTH = 420;

const RECORD_TYPE_VALUES: UnpaidInvoiceRecordTypeFilter[] = [
  'all',
  'invoices',
  'creditMemos',
];

const RECORD_TYPE_LABEL_KEYS: Record<UnpaidInvoiceRecordTypeFilter, string> = {
  all: 'all',
  creditMemos: 'finance_hub.invoices.filters.type_credit_memos',
  invoices: 'finance_hub.invoices.filters.type_invoices',
};

export const countActiveUnpaidInvoiceFilters = (
  filters: UnpaidInvoiceFilters,
): number =>
  filters.recordType === DEFAULT_UNPAID_INVOICE_FILTERS.recordType ? 0 : 1;

interface Props {
  filters: UnpaidInvoiceFilters;
  isOpen: boolean;
  onApply: (filters: UnpaidInvoiceFilters) => void;
  onClose: () => void;
}

export const UnpaidInvoicesFilterDialog: React.FC<Props> = ({
  filters,
  isOpen,
  onApply,
  onClose,
}) => {
  // `draft` holds what the user is picking in the dialog. It only becomes
  // real on Apply — Cancel/X just closes. A closed dialog is hidden, not
  // destroyed, so without the reset it would still remember cancelled picks
  const [draft, setDraft] = useState(filters);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const dialogJustOpened = isOpen && !prevIsOpen;

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
  }
  if (dialogJustOpened) {
    setDraft(filters);
  }

  const draftActiveCount = countActiveUnpaidInvoiceFilters(draft);

  const handleRecordTypeChange = (value: string) => {
    const recordType = RECORD_TYPE_VALUES.find(
      (recordTypeValue) => recordTypeValue === value,
    );
    if (recordType) {
      setDraft({ recordType });
    }
  };

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  // SKUI's Dialog reports dismissals (X button, outside click) through
  // onOpenChange; only the "now closed" transition matters to us
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    // disableRemoveScroll prevents the page from shifting while the dialog is open
    <Dialog disableRemoveScroll onOpenChange={handleOpenChange} open={isOpen}>
      <Dialog.Content
        paddingHorizontal={0}
        paddingVertical="$24"
        width={DIALOG_WIDTH}
      >
        <Dialog.Header paddingHorizontal="$24">
          <Dialog.Title>
            {i18n.t('finance_hub.invoices.filters.title')}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Body paddingHorizontal="$24">
          <YStack gap="$4">
            <Typography size="$sm" variant="label">
              {i18n.t('finance_hub.invoices.filters.type')}
            </Typography>
            <SegmentedControl
              aria-label={i18n.t('finance_hub.invoices.filters.type')}
              items={RECORD_TYPE_VALUES.map((recordType) => ({
                label: i18n.t(RECORD_TYPE_LABEL_KEYS[recordType]),
                value: recordType,
              }))}
              onValueChange={handleRecordTypeChange}
              value={draft.recordType}
            />
          </YStack>
        </Dialog.Body>
        <Dialog.Footer paddingHorizontal="$24" paddingTop="$16">
          <XStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Button
              disabled={draftActiveCount === 0}
              onPress={() => setDraft(DEFAULT_UNPAID_INVOICE_FILTERS)}
              variant="tertiary"
            >
              {i18n.t('reset_all')}
            </Button>
            <XStack gap="$8">
              <Dialog.Close asChild>
                <Button variant="secondary">{i18n.t('cancel')}</Button>
              </Dialog.Close>
              <Button onPress={handleApply}>
                {draftActiveCount > 0
                  ? i18n.t('apply_with_count', {
                      count: draftActiveCount,
                    })
                  : i18n.t('apply')}
              </Button>
            </XStack>
          </XStack>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};
